<template>
  <div class="kv-ve-button" ref="root" :class="{ 'is-selected': selected }" :style="{ textAlign: outerAlign }">
    <div
      v-if="editor && selected"
      v-show="toolbarState.visible"
      ref="toolbar"
      class="kv-ve-button__toolbar kv-ve-button__toolbar--bubble"
      :style="toolbarStyle"
      @mousedown.stop="onToolbarMouseDown"
    >
      <select class="kv-ve-button__select" :value="activeFontFamily" @change="setFontFamily($event.target.value)" :title="t('dates-hour')">
        <option value="">{{ t('editor-body_font') }}</option>
        <!-- i18n-validator-ignore-next-line: font name -->
        <option value="Helvetica, Arial, sans-serif">Helvetica</option>
        <!-- i18n-validator-ignore-next-line: font name -->
        <option value="Arial, Helvetica, sans-serif">Arial</option>
        <!-- i18n-validator-ignore-next-line: font name -->
        <option value="Georgia, serif">Georgia</option>
        <!-- i18n-validator-ignore-next-line: font name -->
        <option value="'Times New Roman', Times, serif">Times</option>
      </select>

      <select class="kv-ve-button__select kv-ve-button__select--size" :value="activeFontSize" @change="setFontSize($event.target.value)" :title="t('dates-day')">
        <option value="">—</option>
        <option v-for="n in fontSizes" :key="n" :value="String(n)">{{ n }}</option>
      </select>

      <select class="kv-ve-button__select kv-ve-button__select--lh" :value="activeLineHeight" @change="setLineHeight($event.target.value)" :title="t('editor-line_height')">
        <!-- i18n-validator-ignore-next-line: line height abbreviation -->
        <option value="">LH</option>
        <option value="0.8">0.8</option>
        <option value="0.9">0.9</option>
        <option value="1.0">1.0</option>
        <option value="1.1">1.1</option>
        <option value="1.2">1.2</option>
        <option value="1.3">1.3</option>
        <option value="1.4">1.4</option>
        <option value="1.5">1.5</option>
        <option value="1.6">1.6</option>
        <option value="1.7">1.7</option>
        <option value="1.8">1.8</option>
        <option value="1.9">1.9</option>
        <option value="2.0">2.0</option>
        <option value="2.2">2.2</option>
        <option value="2.4">2.4</option>
        <option value="2.6">2.6</option>
        <option value="2.8">2.8</option>
        <option value="3.0">3.0</option>
      </select>

      <label class="kv-ve-button__color" :title="t('sender_options-token')" :style="{ '--kv-ve-color': activeColor }">
        <input type="color" :value="activeColor" @input="setColor($event.target.value)" />
      </label>

      <span class="kv-ve-button__sep"></span>
      <button type="button" class="kv-ve-button__btn-tool" :class="{ active: editor.isActive('bold') }" @click="toggleBold" :title="t('inputs-editor-bold')" :aria-label="t('inputs-editor-bold')">
        <span class="mdi mdi-format-bold" aria-hidden="true"></span>
      </button>
      <button type="button" class="kv-ve-button__btn-tool" :class="{ active: editor.isActive('italic') }" @click="toggleItalic" :title="t('inputs-editor-italic')" :aria-label="t('inputs-editor-italic')">
        <span class="mdi mdi-format-italic" aria-hidden="true"></span>
      </button>
      <button type="button" class="kv-ve-button__btn-tool" :class="{ active: editor.isActive('underline') }" @click="toggleUnderline" :title="t('inputs-editor-underline')" :aria-label="t('inputs-editor-underline')">
        <span class="mdi mdi-format-underline" aria-hidden="true"></span>
      </button>
      <button type="button" class="kv-ve-button__btn-tool kv-ve-button__btn-tool--strike" :class="{ active: editor.isActive('strike') }" @click="toggleStrike" :title="t('inputs-editor-strike')" :aria-label="t('inputs-editor-strike')">
        <span class="mdi mdi-format-strikethrough" aria-hidden="true"></span>
      </button>
      <!-- No Link button on button block -->
    </div>
    <span
      class="kv-ve-button__btn"
      :style="{
        backgroundColor: block.settings.backgroundColor || 'var(--kv-ve-button-background-color)',
        color: block.settings.color || 'var(--kv-ve-button-text-color)',
        borderRadius: borderRadiusPx,
        padding: paddingPx,
        fontFamily: 'var(--kv-ve-font-family, Helvetica, Arial, sans-serif)',
        fontSize: '14px',
        fontWeight: 600,
        textAlign: textAlign,
        ...borderStyle,
        display: isFill ? 'block' : 'inline-block',
        width: isFill ? '100%' : null,
        boxSizing: isFill ? 'border-box' : null,
      }"
    >
      <EditorContent :editor="editor" class="kv-ve-button__text" />
    </span>
  </div>
</template>

<script>
import { Editor, EditorContent } from "@tiptap/vue-2";
import { getEmailTiptapExtensions } from "../lib/tiptapExtensions";

export default {
  name: "VeButtonBlock",
  components: { EditorContent },
  props: {
    block: { type: Object, required: true },
    selected: { type: Boolean, default: false },
  },
  data() {
    return {
      editor: null,
      fontSizes: [8, 10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48],
      toolbarState: {
        visible: false,
        top: 0,
        left: 0,
        placement: "top",
      },
      _toolbarRaf: null,
    };
  },
  computed: {
    align() {
      const v = String(this.block?.settings?.align || "").toLowerCase();
      // Back-compat: older docs may have stored width as align="fill"
      if (v === "fill") return "fill";
      return v === "left" || v === "center" || v === "right" ? v : null;
    },
    textAlign() {
      // Back-compat: older docs may have stored width as align="fill"
      if (this.align === "fill") return "left";
      return this.align; // null => inherit/default
    },
    isFill() {
      const w = this.block?.settings?.width;
      // Back-compat: older docs may have stored width as align="fill"
      return w === "filled" || this.align === "fill";
    },
    outerAlign() {
      // "fill" is width behavior; outer align only applies for inline buttons.
      return this.isFill ? "left" : this.align; // null => inherit from column text-align
    },
    borderRadiusPx() {
      const v = this.block?.settings?.borderRadius;
      // Support both number and object (custom corners)
      if (typeof v === "object" && !Array.isArray(v)) {
        const topLeft = Number(v.topLeft) || 0;
        const topRight = Number(v.topRight) || 0;
        const bottomLeft = Number(v.bottomLeft) || 0;
        const bottomRight = Number(v.bottomRight) || 0;
        return `${topLeft}px ${topRight}px ${bottomRight}px ${bottomLeft}px`;
      }
      const n = Number(v);
      const px = Number.isFinite(n) ? n : 8;
      return `${Math.max(0, px)}px`;
    },
    paddingPx() {
      const py = Number(this.block?.settings?.paddingY);
      const px = Number(this.block?.settings?.paddingX);
      const y = Number.isFinite(py) ? py : 12;
      const x = Number.isFinite(px) ? px : 18;
      return `${Math.max(0, y)}px ${Math.max(0, x)}px`;
    },
    borderStyle() {
      const b = this.block?.settings?.border || {};
      if (!b.enabled) return { border: "0" };
      const styleRaw = String(b.style || "solid").toLowerCase();
      const style = styleRaw === "dashed" || styleRaw === "dotted" ? styleRaw : "solid";
      const color = typeof b.color === "string" && b.color ? b.color : "#e5e7eb";
      const wRaw = Number(b.width ?? 1);
      const width = Number.isFinite(wRaw) ? Math.min(20, Math.max(1, Math.round(wRaw))) : 1;
      const sides = b.sides || {};
      const isOn = (k) => sides[k] !== false;
      const css = (on) => (on ? `${width}px ${style} ${color}` : null);
      return {
        borderTop: css(isOn("top")),
        borderRight: css(isOn("right")),
        borderBottom: css(isOn("bottom")),
        borderLeft: css(isOn("left")),
      };
    },
    activeColor() {
      if (!this.editor) return "#111827";
      return this.editor.getAttributes("textStyle")?.color || "#111827";
    },
    activeFontFamily() {
      if (!this.editor) return "";
      return this.editor.getAttributes("textStyle")?.fontFamily || "";
    },
    activeFontSize() {
      if (!this.editor) return "";
      const fs = this.editor.getAttributes("textStyle")?.fontSize || "";
      const m = String(fs).match(/^(\d+)(px)?$/);
      return m ? m[1] : "";
    },
    activeLineHeight() {
      if (!this.editor) return "";
      return this.editor.getAttributes("textStyle")?.lineHeight || "";
    },
    toolbarStyle() {
      const t = this.toolbarState;
      return {
        top: `${Math.round(t.top)}px`,
        left: `${Math.round(t.left)}px`,
        transform: "translate(-50%, calc(-100% - 10px))",
      };
    },
  },
  watch: {
    selected: {
      immediate: true,
      handler(next) {
        if (this.editor) this.editor.setOptions({ editable: Boolean(next) });
        if (!next) this.toolbarState.visible = false;
        else this.$nextTick(() => this.scheduleToolbarUpdate());
      },
    },
  },
  mounted() {
    if (!this.block.settings) this.$set(this.block, "settings", {});
    const incoming = this.block?.settings?.content?.tiptapJson;
    const fallbackText = String(this.block?.settings?.text || "").trim() || "Button";
    const tiptapJson =
      incoming || {
        type: "doc",
        content: [{ type: "paragraph", content: [{ type: "text", text: fallbackText }] }],
      };

    this.editor = new Editor({
      extensions: getEmailTiptapExtensions({ withPlaceholder: true, withLink: false }),
      editable: Boolean(this.selected),
      editorProps: {
        attributes: {
          style: [
            "font-family: inherit",
            "font-size: inherit",
            "line-height: inherit",
            "font-weight: inherit",
            "color: inherit",
            "text-transform: none",
            "letter-spacing: normal",
            "outline: none",
          ].join(";"),
        },
        handleKeyDown: (_view, event) => {
          if (event?.key === "Enter") {
            event.preventDefault();
            return true;
          }
          return false;
        },
      },
      content: tiptapJson,
      onUpdate: ({ editor }) => {
        if (!this.block.settings) this.$set(this.block, "settings", {});
        this.$set(this.block.settings, "content", { tiptapJson: editor.getJSON() });
        // Keep legacy plain text in sync (used by settings input + back-compat exports)
        const txt = String(editor.getText() || "").replace(/\n/g, " ").trim();
        this.$set(this.block.settings, "text", txt);
      },
    });

    // Bubble toolbar positioning (same behavior as text block: show only on selection)
    this.editor.on?.("selectionUpdate", this.scheduleToolbarUpdate);
    this.editor.on?.("transaction", this.scheduleToolbarUpdate);
    this.editor.on?.("focus", this.scheduleToolbarUpdate);
    this.editor.on?.("blur", ({ event } = {}) => {
      const toolbarEl = this.$refs.toolbar;
      const relatedTarget = event?.relatedTarget || document.activeElement;
      if (toolbarEl && relatedTarget && toolbarEl.contains(relatedTarget)) return;
      this.toolbarState.visible = false;
    });
    window.addEventListener("scroll", this.scheduleToolbarUpdate, true);
    window.addEventListener("resize", this.scheduleToolbarUpdate);
    this.$nextTick(() => this.scheduleToolbarUpdate());
  },
  beforeDestroy() {
    try {
      window.removeEventListener("scroll", this.scheduleToolbarUpdate, true);
      window.removeEventListener("resize", this.scheduleToolbarUpdate);
      if (this._toolbarRaf) window.cancelAnimationFrame(this._toolbarRaf);
      this.editor?.destroy?.();
    } catch (e) {
      // ignore
    }
  },
  methods: {
    t(term, replaces) {
      return this.$root.t(term, replaces);
    },
    onToolbarMouseDown(e) {
      const t = e?.target;
      const isFormControl = t?.closest?.("select, input, textarea, label");
      if (isFormControl) return;
      e?.preventDefault?.();
    },
    scheduleToolbarUpdate() {
      if (!this.editor || !this.selected) return;
      if (this._toolbarRaf) return;
      this._toolbarRaf = window.requestAnimationFrame(() => {
        this._toolbarRaf = null;
        this.updateToolbarPosition();
      });
    },
    updateToolbarPosition() {
      if (!this.editor || !this.selected) {
        this.toolbarState.visible = false;
        return;
      }
      const isFocused = this.editor?.isFocused;
      const focused = typeof isFocused === "function" ? Boolean(isFocused.call(this.editor)) : Boolean(isFocused);
      const toolbarEl = this.$refs.toolbar;
      const toolbarHasFocus = Boolean(toolbarEl && document.activeElement && toolbarEl.contains(document.activeElement));
      if (!focused && !toolbarHasFocus) {
        this.toolbarState.visible = false;
        return;
      }
      const sel = this.editor?.state?.selection;
      if (sel?.empty && !toolbarHasFocus) {
        this.toolbarState.visible = false;
        return;
      }
      const rootEl = this.$refs.root;
      const view = this.editor?.view;
      if (!rootEl || !toolbarEl || !view) return;
      try {
        const { from, to } = this.editor.state.selection;
        const a = view.coordsAtPos(from);
        const b = view.coordsAtPos(to);
        const box = {
          left: Math.min(a.left, b.left),
          right: Math.max(a.right, b.right),
          top: Math.min(a.top, b.top),
          bottom: Math.max(a.bottom, b.bottom),
        };
        const toolbarRect = toolbarEl.getBoundingClientRect();
        const anchorX = (box.left + box.right) / 2;
        const anchorTop = box.top;
        const gap = 10;
        const pad = 12;
        const viewportW = window.innerWidth || document.documentElement?.clientWidth || 0;
        let left = anchorX;
        const minLeft = pad + toolbarRect.width / 2;
        const maxLeft = Math.max(minLeft, viewportW - pad - toolbarRect.width / 2);
        left = Math.min(maxLeft, Math.max(minLeft, left));
        const minTop = toolbarRect.height + gap + pad;
        const top = Math.max(anchorTop, minTop);
        this.toolbarState.visible = true;
        this.toolbarState.left = left;
        this.toolbarState.top = top;
        this.toolbarState.placement = "top";
      } catch (e) {
        this.toolbarState.visible = false;
      }
    },
    toggleBold() {
      this.editor?.chain().focus().toggleBold().run();
    },
    toggleItalic() {
      this.editor?.chain().focus().toggleItalic().run();
    },
    toggleUnderline() {
      this.editor?.chain().focus().toggleUnderline().run();
    },
    toggleStrike() {
      this.editor?.chain().focus().toggleStrike().run();
    },
    setColor(color) {
      if (!this.editor) return;
      this.editor.chain().focus().setColor(color).run();
    },
    setFontFamily(fontFamily) {
      if (!this.editor) return;
      const v = String(fontFamily || "").trim();
      if (!v) this.editor.chain().focus().unsetFontFamily().run();
      else this.editor.chain().focus().setFontFamily(v).run();
    },
    setFontSize(size) {
      if (!this.editor) return;
      const n = Number(size);
      if (!Number.isFinite(n) || n <= 0) {
        this.editor.chain().focus().unsetFontSize().run();
        return;
      }
      this.editor.chain().focus().setFontSize(`${n}px`).run();
    },
    setLineHeight(raw) {
      if (!this.editor) return;
      const v = String(raw || "").trim();
      if (!v) {
        this.editor.chain().focus().unsetLineHeight().run();
        return;
      }
      this.editor.chain().focus().setLineHeight(v).run();
    },
  },
};
</script>

<style scoped lang="less">
.kv-ve-button__btn {
  display: inline-block;
  font-weight: 600;
  font-size: 14px;
}
.kv-ve-button__text :deep(p) {
  margin: 0;
}
.kv-ve-button__text :deep(.ProseMirror) {
  outline: none;
  white-space: nowrap;
}

.kv-ve-button__toolbar--bubble {
  position: fixed;
  z-index: 10050;
}
.kv-ve-button__select {
  height: 32px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 0 10px;
  font-size: 12px;
  background: #fff;
  color: #101828;
}
.kv-ve-button__select--size {
  width: 64px;
}
.kv-ve-button__select--lh {
  width: 76px;
}
.kv-ve-button__sep {
  width: 1px;
  height: 18px;
  background: #e5e7eb;
  display: inline-block;
  margin: 0 2px;
}
.kv-ve-button__color {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: #fff;
  display: grid;
  place-items: center;
  cursor: pointer;
}
.kv-ve-button__color input[type="color"] {
  width: 18px;
  height: 18px;
  padding: 0;
  border: 0;
  background: transparent;
}
.kv-ve-button__btn-tool {
  all: unset;
  cursor: pointer;
  width: 30px;
  height: 30px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  display: grid;
  place-items: center;
  background: #fff;
}
.kv-ve-button__btn-tool--strike .mdi {
  font-size: 16px;
}
.kv-ve-button__btn-tool.active {
  border-color: rgba(42, 170, 76, 0.85);
  box-shadow: 0 0 0 4px rgba(42, 170, 76, 0.16);
}

/* Bubble toolbar container visuals (match text block look) */
.kv-ve-button__toolbar {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #ffffff;
  border: 1px solid rgba(234, 236, 240, 0.9);
  border-radius: 14px;
  padding: 8px;
  box-shadow: 0 10px 30px rgba(16, 24, 40, 0.12);
  backdrop-filter: blur(6px);
}
</style>


